'use client';

import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function CreateServiceTypeTemplateDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { mutate, isPending } = api.serviceTypeTemplates.createServiceTypeTemplatesPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Service type created');
        api.serviceTypeTemplates.getAllServiceTypeTemplatesGet.invalidateQueries();
        setOpen(false);
      },
    }
  );
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isDisabled = useMemo(() => {
    if (!name) return true;
    if (isPending) return true;
    return false;
  }, [name, isPending]);

  const onSubmit = () => {
    if (!name) {
      toast.error('Name is required');
      return;
    }

    mutate({
      office_id: officeId,
      name: name,
      description: description,
      status: 'ACTIVE',
      frequency_unit: 'MANUAL',
      ending_type: 'NONE',
      customer_presence_required: false,
      reschedule_allowed: false,
      reschedule_require_same_month: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>
          <PlusIcon className='size-4' />
          Add Service Type
        </Button>
      </DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Add Service Type</DialogTitle>
          <DialogDescription>Add a new service type to your office.</DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='name'>Name</Label>
            <Input id='name' value={name} onChange={(e) => setName(e.target.value)} className='col-span-3' />
          </div>
          <div className='grid w-full grid-cols-4 gap-2'>
            <Label htmlFor='description' className='place-self-start pt-2'>
              Description
            </Label>
            <Textarea
              id='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Enter description (optional)'
              className='col-span-3 w-full text-sm'
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Creating...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
